/**
 * Video in context (sections 4 and 5): upload a file right here or choose one from Files. The
 * item names its video as soon as the upload starts, and shows where it stands: Uploading 42%,
 * Waiting for the encoder (the studio's Mac encodes), Encoding, Ready. When it is ready its length,
 * captions and scrub previews are read from it; the length is never typed.
 *
 * Live events also take a stream address (a live stream is not an upload).
 */

import { useEffect, useRef, useState } from "react";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import Notice from "@web-ui/components/Notice";
import Segmented from "@web-ui/components/Segmented";
import { explainMediaError } from "@/media/errors";
import { FilePreview } from "@/media/file-preview";
import { MediaPicker } from "@/media/media-picker";
import { useUploads } from "@/media/upload-center";
import { UploadStateLine } from "@/media/upload-state";
import { useStudio } from "@/shell/studio";
import { useForm } from "../form-context";
import { fieldDomId, getPath, setPath, type Doc } from "../paths";
import type { SectionPlan } from "../plan";
import { lengthWords } from "../time";
import { Group } from "../controls/inputs";
import { nextId } from "../moments";

interface VideoRef { host: string; videoId: string; url?: string }

/** The parts of a ready video's feed snippet an item takes over: its length, captions and scrub previews. */
export function applyVideoSnippet(doc: Doc, snippet: Record<string, unknown>, path = "playback"): Doc {
  const playback = { ...((getPath(doc, path) as Doc | undefined) ?? {}) };
  const from = (snippet["playback"] ?? {}) as Doc;
  playback["video"] = from["video"];
  if (from["captions"]) playback["captions"] = from["captions"]; else delete playback["captions"];
  if (from["storyboard"]) playback["storyboard"] = from["storyboard"]; else delete playback["storyboard"];
  let next = setPath(doc, path, playback);
  if (path === "playback" && typeof snippet["durationSeconds"] === "number") next = { ...next, durationSeconds: snippet["durationSeconds"] };
  return next;
}

/** One video slot: the main video, a trailer, a live event's stream or replay, an angle's picture. */
export function VideoSlot({ path, label, help, ladder, streams, derive, optional }: {
  /** Where the VideoRef sits, e.g. "playback.video", "live.video". */
  path: string;
  label: string;
  help?: string;
  ladder: string;
  /** Offer "a stream address" (live). */
  streams?: boolean;
  /** Take length, captions and scrub previews from the video when it is ready (a title's main video). */
  derive?: boolean;
  optional?: boolean;
}) {
  const form = useForm();
  const { pack } = useStudio();
  const center = useUploads();
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState("");
  const [address, setAddress] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const ref = getPath(form.doc, path) as VideoRef | undefined;
  const parent = path.replace(/\.video$|\.preview$/, "");
  const own = ref?.host === "r2-hls" && ref.videoId ? ref.videoId : null;
  const asset = own ? center.asset(own) : undefined;
  const state = own ? center.stateOf(own) : null;
  const issue = form.issue(`${path}.videoId`)?.message ?? form.issue(path)?.message;

  // A video that became ready while the item was open: take over its length and captions.
  const duration = form.doc["durationSeconds"] as number | undefined;
  useEffect(() => {
    if (!derive || !form.canEdit || !asset?.feed || asset.status !== "ready") return;
    const length = asset.feed["durationSeconds"];
    if (typeof length === "number" && length !== duration) form.update((d) => applyVideoSnippet(d, asset.feed!));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derive, asset?.status, asset?.feed, duration]);

  const attach = (videoId: string, snippet?: Record<string, unknown> | null) => {
    form.update((d) => {
      if (derive && snippet) return applyVideoSnippet(d, snippet, parent);
      let next = setPath(d, path, { host: "r2-hls", videoId });
      if (derive) { const { durationSeconds: _gone, ...rest } = next; next = rest; }
      return next;
    });
    form.touch(`${path}.videoId`);
  };

  const upload = async (file: File) => {
    setError("");
    const name = (form.doc["title"] as Record<string, string> | undefined)?.[form.locale] || file.name.replace(/\.[^.]+$/, "");
    try { attach(await center.startVideo(file, name, ladder)); }
    catch (e) { setError(explainMediaError(e)); }
    finally { if (input.current) input.current.value = ""; }
  };

  const assetName = asset?.title ?? (own ? "A video from Files" : undefined);
  const length = derive ? duration ?? ((asset?.details as { durationSeconds?: number } | null)?.durationSeconds) : (asset?.details as { durationSeconds?: number } | null)?.durationSeconds;

  return (
    <Group path={path} label={label} help={help} optional={optional} error={issue}>
      {ref?.videoId ? (
        <div className="flex flex-wrap items-start gap-3 rounded-2xl bg-b-surface1 p-3">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-b-surface3" aria-hidden="true">▶</span>
          <div className="flex min-w-0 grow flex-col gap-1">
            {own ? <span className="truncate text-body-2 font-medium">{asset ? assetName : "A video added another way"}</span> : <span className="text-body-2 font-medium">{ref.host === "url" ? "A stream address" : "A video on another service"}</span>}
            {own && state ? <UploadStateLine state={state} showDetail={state.key !== "ready"} /> : null}
            {own && !state && center.assets ? <p className="text-caption text-t-secondary">Not in Files: it was added another way (for example with an imported catalogue).</p> : null}
            {!own ? <p className="truncate text-caption text-t-secondary">{ref.videoId}</p> : null}
            {length ? <p className="text-caption text-t-secondary">{`Length ${lengthWords(length)} · read from the video`}</p> : null}
            {derive && state?.key === "ready" && Array.isArray((getPath(form.doc, "playback.captions") as unknown[] | undefined)) ? <p className="text-caption text-t-secondary">{`${(getPath(form.doc, "playback.captions") as unknown[]).length} caption tracks, from the video`}</p> : null}
          </div>
          {form.canEdit ? (
            <div className="flex flex-wrap gap-1">
              {asset ? <Button type="button" size="xs" variant="secondary" onClick={() => setPreviewing(true)}>Preview</Button> : null}
              <Button type="button" size="xs" variant="ghost" onClick={() => input.current?.click()}>Replace</Button>
              <Button type="button" size="xs" variant="ghost" onClick={() => setPicking(true)}>Choose another</Button>
              <Button type="button" size="xs" variant="ghost" onClick={() => form.update((d) => { let next = setPath(d, path, undefined); if (derive) { const { durationSeconds: _gone, ...rest } = next; next = rest; } return next; })}>Remove</Button>
            </div>
          ) : null}
        </div>
      ) : form.canEdit ? (
        <div className="flex flex-col gap-3 rounded-2xl border-2 border-dashed border-s-stroke2 p-4">
          <p className="text-body-2 text-t-secondary">{derive ? `No video yet. You can save the ${pack.nouns.title.one.toLowerCase()} as a draft without one.` : "None yet."}</p>
          {streams ? <Segmented size="sm" label="Where the video comes from" options={[{ id: "upload", label: "An upload" }, { id: "address", label: "A stream address" }]} value={address ? "address" : "upload"} onChange={(v) => setAddress(v === "address")} /> : null}
          {address ? (
            <StreamAddress onUse={(url) => form.set(path, { host: "url", videoId: url })} />
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="secondary" icon="upload" onClick={() => input.current?.click()}>Upload a video</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPicking(true)}>Choose from Files</Button>
            </div>
          )}
        </div>
      ) : <p className="text-body-2 text-t-secondary">No video yet.</p>}
      <input ref={input} id={fieldDomId(`${path}.videoId`)} type="file" accept="video/*" className="sr-only" tabIndex={-1} aria-label={`${label}: video file`} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
      {error ? <Notice tone="critical" onDismiss={() => setError("")}>{error}</Notice> : null}
      <FilePreview asset={previewing ? asset ?? null : null} onClose={() => setPreviewing(false)} />
      <MediaPicker kind="video" open={picking} onClose={() => setPicking(false)} onPick={(a) => attach(a.id, a.status === "ready" ? a.feed : null)} />
    </Group>
  );
}

function StreamAddress({ onUse }: { onUse: (url: string) => void }) {
  const [url, setUrl] = useState("");
  const ok = /^https:\/\/\S+$/.test(url.trim());
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field className="grow" label="Stream address" size="sm" placeholder="https://…/live.m3u8" description="The HLS address your streaming service gives you." value={url} onChange={(e) => setUrl(e.target.value)} />
      <Button type="button" size="sm" variant="secondary" disabled={!ok} onClick={() => onUse(url.trim())}>Use it</Button>
    </div>
  );
}

export function VideoSection({ section }: { section: Extract<SectionPlan, { id: "video" }> }) {
  const { pack } = useStudio();
  return (
    <div className="flex flex-col gap-6">
      <VideoSlot path="playback.video" label="Video" ladder={pack.vertical === "film" ? "hero" : "standard"} derive />
      {section.trailer ? <VideoSlot path="playback.preview" label="Trailer" optional help="A short clip that plays while viewers browse past it." ladder="preview" /> : null}
    </div>
  );
}

interface Feed { id: string; kind: "angle" | "stage"; title: Record<string, string>; live?: { video?: VideoRef }; replay?: { video?: VideoRef } }

export function StreamsSection({ section }: { section: Extract<SectionPlan, { id: "streams" }> }) {
  const form = useForm();
  const recording = form.doc["liveMode"] === "recording";
  const feeds = (form.doc["feeds"] as Feed[] | undefined) ?? [];
  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-md">
        <Segmented label="Live is" options={[{ id: "stream", label: "A live stream" }, { id: "recording", label: "A recording, played as live" }]} value={recording ? "recording" : "stream"} disabled={!form.canEdit}
          onChange={(v) => form.set("liveMode", v === "recording" ? "recording" : undefined)} />
        <p className="mt-1 text-caption text-t-secondary">{recording ? "Viewers who tune in late join where the recording has got to, as if it were live." : "Your streaming service's live picture."}</p>
      </div>
      <VideoSlot path="live.video" label={recording ? "The recording" : "Live stream"} ladder="standard" streams={!recording} optional />
      <VideoSlot path="replay.video" label="Replay" help="What viewers watch after it has ended." ladder="standard" optional />
      <Field id={fieldDomId("replayDays")} label="Replay stays for (days)" type="number" min={1} optional description="Leave empty to keep it." value={typeof form.doc["replayDays"] === "number" ? String(form.doc["replayDays"]) : ""} disabled={!form.canEdit}
        onChange={(e) => form.set("replayDays", e.target.value === "" ? undefined : Number(e.target.value))} />
      <Group path="feeds" label={`${section.feedWord}s`} optional help={section.feedKind === "stage" ? "Other stages streaming at the same time; viewers switch between them." : "Other camera angles beside the main picture; viewers switch between them."}>
        {feeds.map((f, i) => (
          <div key={f.id} className="flex flex-col gap-3 rounded-2xl border border-s-subtle p-4">
            <div className="flex items-end gap-2">
              <Field className="grow" label={`${section.feedWord} ${i + 1}: name`} size="sm" value={f.title?.[form.locale] ?? ""} disabled={!form.canEdit}
                onChange={(e) => form.set(`feeds.${i}.title`, { ...(f.title ?? {}), [form.locale]: e.target.value })} />
              {form.canEdit ? <Button type="button" size="xs" variant="ghost" icon="close" isIconOnly aria-label={`Remove ${section.feedWord.toLowerCase()} ${i + 1}`} onClick={() => form.set("feeds", feeds.filter((_, j) => j !== i))} /> : null}
            </div>
            <VideoSlot path={`feeds.${i}.live.video`} label="Live" ladder="standard" streams optional />
            <VideoSlot path={`feeds.${i}.replay.video`} label="Replay" ladder="standard" optional />
          </div>
        ))}
        {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => form.set("feeds", [...feeds, { id: nextId(section.feedKind, feeds.map((f) => f.id)), kind: section.feedKind, title: { [form.locale]: `${section.feedWord} ${feeds.length + 1}` } }])}>{`Add a ${section.feedWord.toLowerCase()}`}</Button></div> : null}
      </Group>
    </div>
  );
}
